'use client';

import { useEffect, useState } from 'react';

export interface CurrentUser {
  id: string;
  role: string | null;
}

/**
 * Module-level cache so a page full of forwarder cards shares ONE /api/auth/me
 * request instead of firing one per card.
 */
let inflight: Promise<CurrentUser | null> | null = null;

function fetchCurrentUser(): Promise<CurrentUser | null> {
  if (!inflight) {
    inflight = fetch('/api/auth/me')
      .then((r) => r.json())
      .then((data) => {
        const u = data?.user ?? null;
        return u ? { id: u.id as string, role: (u.role ?? null) as string | null } : null;
      })
      .catch(() => null);
  }
  return inflight;
}

/**
 * Client-side viewer lookup for statically rendered (ISR) pages.
 *
 * Public SEO pages such as /[country] must NOT call `supabase.auth.getUser()`
 * in their server component: reading cookies opts the whole route out of the
 * Vercel CDN cache, so every visitor pays a full dynamic render (a long chain
 * of Supabase round-trips, plus a cold start on a low-traffic path). Resolving
 * the viewer here instead keeps the HTML cacheable and the listing fully
 * present in the static markup for crawlers.
 */
export function useCurrentUser() {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchCurrentUser().then((u) => {
      if (cancelled) return;
      setUser(u);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    userId: user?.id ?? null,
    isForwarder: user?.role === 'forwarder',
    isLoading,
  };
}
